// A hand-mirror of src/lister/card/condition.ts — the SERVER's source of truth for the fixed
// condition ladder a RAW (ungraded) card is priced and listed against. `ui/` does not import
// from `src/` (ui/tsconfig.json's `include` is ["src", "../tests/ui"], and no file under
// ui/src crosses that boundary), so this list is a second, hand-kept copy rather than a shared
// import — the same shape cardFactsTitle already carries for cardTitle (src/lister/card/identity.ts).
// It is kept honest the same way: tests/ui/lister-card-title.test.ts imports the real server
// module directly (a TEST file, which sits outside ui/'s own boundary) and pins every code and
// every label here to it.
//
// The operator picks the condition; the model never proposes one (src/lister/card/condition.ts's
// own comment explains why — ambiguous two-letter tokens like "HP" and "EX" mean other things on
// a card). Nothing in this module, or in anything that reads it, may supply a default.
export const CARD_CONDITIONS = ["NM", "LP", "MP", "HP", "DMG"] as const;
export type ListerCardCondition = (typeof CARD_CONDITIONS)[number];
// "tcg" — a playable trading card game (Pokémon, One Piece, Dragon Ball, Magic, Yu-Gi-Oh,
// Lorcana). "non_sports" — a collect-only set with no gameplay (Garbage Pail Kids, Marvel,
// Star Wars). Both speak the TCG condition dialect below; only "sports" gets its own.
export type ListerCardCategory = "tcg" | "sports" | "non_sports";

const TCG_LABELS: Record<ListerCardCondition, string> = {
  NM: "Near Mint", LP: "Lightly Played", MP: "Moderately Played", HP: "Heavily Played", DMG: "Damaged",
};
const SPORTS_LABELS: Record<ListerCardCondition, string> = {
  NM: "Near Mint or Better", LP: "Excellent", MP: "Very Good", HP: "Poor", DMG: "Damaged",
};

/** The select's options, best to worst — the order CARD_CONDITIONS itself is written in. */
export function conditionChoices(category: ListerCardCategory): Array<{ code: ListerCardCondition; label: string }> {
  const labels = category === "sports" ? SPORTS_LABELS : TCG_LABELS;
  return CARD_CONDITIONS.map((code) => ({ code, label: labels[code] }));
}
